import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClipboardDocumentIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { searchSupportDebugUsersAsync } from "../../../store/thunks/SupportDebugThunk";
import { SupportDebugUserSearchResult } from "../../../services/SupportDebugService";
import { useToast } from "../../../contexts/ToastContext";
import { Badge } from "../../Catalyst/badge";
import { Button } from "../../Catalyst/button";
import { Input, InputGroup } from "../../Catalyst/input";
import { LoadingState } from "./PanelStates";
import { EM_DASH, formatDate, userStatusBadgeColor, UUID_REGEX } from "./utils";

interface UserLookupProps {
  onSelectUser: (userId: string) => void;
  onChangeUser: () => void;
}

const displayName = (first: string | null, last: string | null, email: string): string => {
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || email;
};

const UserLookup = ({ onSelectUser, onChangeUser }: UserLookupProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccessToast, showErrorToast } = useToast();

  const selectedUser = useSelector((state: RootState) => state.SupportDebugStore.selectedUser);
  const userStatus = useSelector((state: RootState) => state.SupportDebugStore.userStatus);
  const userError = useSelector((state: RootState) => state.SupportDebugStore.userError);
  const searchResults = useSelector((state: RootState) => state.SupportDebugStore.searchResults);
  const searchStatus = useSelector((state: RootState) => state.SupportDebugStore.searchStatus);
  const searchError = useSelector((state: RootState) => state.SupportDebugStore.searchError);
  // The lowercased email the current results answer. Lives in the slice (set on
  // search pending, cleared when a user-detail fetch SUCCEEDS) so a failed
  // selection leaves the result list on screen alongside the error.
  const submittedEmail = useSelector(
    (state: RootState) => state.SupportDebugStore.searchSubmittedFor
  );
  const searchTotalItems = useSelector(
    (state: RootState) => state.SupportDebugStore.searchTotalItems
  );

  const [input, setInput] = useState("");

  // Exactly one exact-email match -> select it without making the admin click.
  useEffect(() => {
    if (!submittedEmail || searchStatus !== STATUS.SUCCESS) return;
    const exactMatches = searchResults.filter(
      (user) => (user.email ?? "").toLowerCase() === submittedEmail
    );
    if (exactMatches.length === 1) {
      onSelectUser(exactMatches[0].id);
    }
  }, [submittedEmail, searchStatus, searchResults, onSelectUser]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    if (UUID_REGEX.test(value)) {
      onSelectUser(value);
    } else {
      dispatch(searchSupportDebugUsersAsync(value.toLowerCase()));
    }
  };

  // Deliberately does NOT clear the search results: they stay visible until the
  // user-detail fetch succeeds (cleared in the slice), so a failed load keeps
  // the list around for a retry.
  const handlePickResult = (user: SupportDebugUserSearchResult) => {
    onSelectUser(user.id);
  };

  const handleChangeUser = () => {
    setInput("");
    onChangeUser(); // resets the slice, which clears the search state too
  };

  const handleCopyUserId = async () => {
    if (!selectedUser) return;
    try {
      await navigator.clipboard.writeText(selectedUser.id);
      showSuccessToast("Copied", "User ID copied to clipboard");
    } catch {
      showErrorToast("Copy failed", "Could not copy the user ID to the clipboard");
    }
  };

  const renderSelectedUser = () => {
    if (!selectedUser) return null;
    return (
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-base font-semibold text-texthead">
              {displayName(selectedUser.first, selectedUser.last, selectedUser.email)}
            </span>
            {selectedUser.userStatus && (
              <Badge color={userStatusBadgeColor(selectedUser.userStatus)}>
                {selectedUser.userStatus}
              </Badge>
            )}
            {/* Detail endpoint marshals camelCase isActive. Render the badge
                only when the field is actually a boolean — a missing value
                must never be presented as "Inactive". */}
            {typeof selectedUser.isActive === "boolean" && (
              <Badge color={selectedUser.isActive ? "green" : "red"}>
                {selectedUser.isActive ? "Active" : "Inactive"}
              </Badge>
            )}
          </div>
          <div className="mt-1 text-sm text-textColor">{selectedUser.email}</div>
          <div className="mt-3 grid grid-cols-1 gap-x-10 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <span className="font-medium text-textColor3">Organization: </span>
              <span className="text-texthead">{selectedUser.organization?.name ?? EM_DASH}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-textColor3">User ID: </span>
              <span className="font-mono text-xs text-texthead">{selectedUser.id}</span>
              <button
                type="button"
                onClick={handleCopyUserId}
                title="Copy user ID"
                aria-label="Copy user ID"
                className="rounded p-0.5 text-zinc-500 hover:bg-gray-100 hover:text-zinc-700"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
              </button>
            </div>
            <div>
              <span className="font-medium text-textColor3">Last payday: </span>
              <span className="text-texthead">{formatDate(selectedUser.last_payday)}</span>
            </div>
            <div>
              <span className="font-medium text-textColor3">Pay frequency: </span>
              <span className="text-texthead">{selectedUser.pay_frequency ?? EM_DASH}</span>
            </div>
          </div>
        </div>
        <Button outline onClick={handleChangeUser}>
          Change user
        </Button>
      </div>
    );
  };

  const renderSearchArea = () => (
    <>
      {userStatus === STATUS.FAILED && (
        <div className="mb-4 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-400" />
          <p className="text-sm font-medium text-red-500">
            {userError || "Could not load that user. Please try again."}
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-[490px]">
          <InputGroup>
            <MagnifyingGlassIcon />
            <Input
              name="userLookup"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="User email or user ID"
              aria-label="User email or user ID"
            />
          </InputGroup>
        </div>
        <Button color="dark" type="submit" disabled={!input.trim()}>
          Search
        </Button>
      </form>

      {submittedEmail && searchStatus === STATUS.LOADING && <LoadingState />}

      {submittedEmail && searchStatus === STATUS.FAILED && (
        <div className="mt-4 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-400" />
          <p className="text-sm font-medium text-red-500">
            {searchError || "Something went wrong while searching. Please try again."}
          </p>
        </div>
      )}

      {submittedEmail && searchStatus === STATUS.SUCCESS && searchResults.length === 0 && (
        <p className="mt-4 text-sm font-medium text-texthead opacity-70">No user found</p>
      )}

      {submittedEmail && searchStatus === STATUS.SUCCESS && searchResults.length > 0 && (
        <>
          <ul className="mt-4 divide-y divide-shadowborder overflow-hidden rounded-lg border border-shadowborder">
            {searchResults.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  onClick={() => handlePickResult(user)}
                  className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-texthead">
                      {displayName(user.first, user.last, user.email)}
                    </span>
                    <span className="block text-sm text-textColor">{user.email}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3 text-sm text-textColor3">
                    <span>Org #{user.organization_id ?? EM_DASH}</span>
                    {user.user_status && (
                      <Badge color={userStatusBadgeColor(user.user_status)}>
                        {user.user_status}
                      </Badge>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {/* The list endpoint is paginated (per_page=10) — flag truncation.
              total_items is optional on the response, so fall back to a
              full-page heuristic when it's missing. */}
          {searchTotalItems != null && searchTotalItems > searchResults.length && (
            <p className="mt-2 text-sm text-textColor3">
              Showing first {searchResults.length} of {searchTotalItems} matches {EM_DASH} refine
              the search.
            </p>
          )}
          {searchTotalItems == null && searchResults.length === 10 && (
            <p className="mt-2 text-sm text-textColor3">
              Showing first 10 matches {EM_DASH} refine the search if the user isn&rsquo;t listed.
            </p>
          )}
        </>
      )}
    </>
  );

  return (
    <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
      <div className="px-6 pb-5">
        <div className="desktext11 text-texthead">User lookup</div>
        <p className="mt-1 font-inter text-sm font-normal leading-5 text-textColor">
          Find the user whose app data you want to inspect
        </p>
        <div className="mt-4">
          {userStatus === STATUS.LOADING ? (
            <LoadingState />
          ) : selectedUser ? (
            renderSelectedUser()
          ) : (
            renderSearchArea()
          )}
        </div>
      </div>
    </div>
  );
};

export default UserLookup;
